import axios from 'axios';
import {
  EntryHistory, ExportCount, ExportFormat, InactiveResponse, JournalFilters, JournalListResponse, JournalMeta, JournalSummary,
  OptionField, OptionItem, PharmaciesResponse, ProductResponse, SortState, StructureResponse, TimelineBy, TimelineResponse
} from './types';

/** Сервис документов: с портала запросы идут через основной бэкенд (/api/v2/journal → :3003) */
const BASE = '/api/v2/journal';

/** Фильтры → параметры запроса (пустые значения отбрасываются); skip - поле, для которого подбираются значения */
export function filtersToParams(f: JournalFilters, skip?: OptionField): Record<string, string> {
  const p: Record<string, string> = {};
  const put = (key: string, value: string) => { if (value) p[key] = value; };
  put('month', f.month);
  put('pharmacy', f.pharmacies.join(','));
  put('search', f.search.trim());
  put('bucket', f.bucket);
  put('expiryFrom', f.expiryFrom);
  put('expiryTo', f.expiryTo);
  (Object.keys(f.picks) as OptionField[]).forEach(field => {
    const pick = f.picks[field];
    if (pick && field !== skip) put(field, pick.value);
  });
  return p;
}

/** Повторная загрузка того же набора фильтров (страница, сортировка, «Обновить») в журнал действий не пишется */
const silentParam = (silent: boolean): Record<string, string> => (silent ? { silent: '1' } : {});

export interface DownloadedFile { blob: Blob; filename: string }

/** Расширение по типу содержимого ответа: Content-Type доступен браузеру всегда, в отличие от Content-Disposition */
function extensionFrom(contentType: string, format: ExportFormat): string {
  if (/zip/i.test(contentType)) return 'zip';
  if (/spreadsheetml/i.test(contentType)) return 'xlsx';
  if (/csv/i.test(contentType)) return 'csv';
  return format;
}

/**
 * Имя файла из Content-Disposition: сначала русское (filename*=UTF-8''…), затем ASCII (filename="…").
 * Если браузер заголовок скрыл (запрос между разными адресами в dev), имя собирается по типу содержимого.
 */
function fileNameFrom(disposition: unknown, contentType: string, format: ExportFormat): string {
  const header = String(disposition || '');
  const utf = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf) {
    try {
      return decodeURIComponent(utf[1].trim());
    } catch {
      // некорректная кодировка - берём ASCII-имя
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  if (plain) return plain[1];
  return `expiry_journal_${new Date().toISOString().slice(0, 10)}.${extensionFrom(contentType, format)}`;
}

export const journalApi = {
  async meta(signal?: AbortSignal): Promise<JournalMeta> {
    const res = await axios.get(`${BASE}/meta`, { signal });
    return res.data;
  },

  async list(filters: JournalFilters, sort: SortState, page: number, limit: number, silent: boolean, signal?: AbortSignal): Promise<JournalListResponse> {
    const params = {
      ...filtersToParams(filters), ...silentParam(silent),
      sortBy: sort.sortBy, sortOrder: sort.sortOrder, page: String(page), limit: String(limit)
    };
    const res = await axios.get(`${BASE}/`, { params, signal });
    return res.data;
  },

  /** KPI по фильтрам: просмотр уже пишет список или аналитика, поэтому сводка всегда без записи в журнал действий */
  async summary(filters: JournalFilters, signal?: AbortSignal): Promise<JournalSummary> {
    const res = await axios.get(`${BASE}/summary`, { params: { ...filtersToParams(filters), silent: '1' }, signal });
    return res.data;
  },

  /** Подбор значений фильтра с учётом остальных фильтров (каскад) */
  async options(field: OptionField, q: string, filters: JournalFilters, signal?: AbortSignal): Promise<OptionItem[]> {
    const res = await axios.get(`${BASE}/options`, { params: { ...filtersToParams(filters, field), field, q }, signal });
    return res.data.options || [];
  },

  async history(id: number, signal?: AbortSignal): Promise<EntryHistory> {
    const res = await axios.get(`${BASE}/entries/${id}/history`, { signal });
    return res.data;
  },

  async timeline(by: TimelineBy, filters: JournalFilters, silent: boolean, signal?: AbortSignal): Promise<TimelineResponse> {
    const res = await axios.get(`${BASE}/analytics/timeline`, { params: { ...filtersToParams(filters), ...silentParam(silent), by }, signal });
    return res.data;
  },

  async structure(filters: JournalFilters, silent: boolean, signal?: AbortSignal): Promise<StructureResponse> {
    const res = await axios.get(`${BASE}/analytics/structure`, { params: { ...filtersToParams(filters), ...silentParam(silent) }, signal });
    return res.data;
  },

  async pharmacies(filters: JournalFilters, silent: boolean, signal?: AbortSignal): Promise<PharmaciesResponse> {
    const res = await axios.get(`${BASE}/analytics/pharmacies`, { params: { ...filtersToParams(filters), ...silentParam(silent) }, signal });
    return res.data;
  },

  /** Карточка товара: фильтр «Товар» заменяется кодом карточки */
  async product(code: string, filters: JournalFilters, silent: boolean, signal?: AbortSignal): Promise<ProductResponse> {
    const res = await axios.get(`${BASE}/analytics/product`, {
      params: { ...filtersToParams(filters, 'product'), ...silentParam(silent), code }, signal
    });
    return res.data;
  },

  async inactive(filters: JournalFilters, silent: boolean, signal?: AbortSignal): Promise<InactiveResponse> {
    const res = await axios.get(`${BASE}/analytics/inactive`, { params: { ...filtersToParams(filters), ...silentParam(silent) }, signal });
    return res.data;
  },

  /** Сколько строк и файлов получится при выгрузке по фильтрам */
  async exportCount(filters: JournalFilters, signal?: AbortSignal): Promise<ExportCount> {
    const res = await axios.get(`${BASE}/export-count`, { params: filtersToParams(filters), signal });
    return res.data;
  },

  /** Один файл либо ZIP со всеми частями - решает сервер (по 100 000 строк в файле) */
  async exportFile(filters: JournalFilters, sort: SortState, format: ExportFormat, columns: string[], signal?: AbortSignal): Promise<DownloadedFile> {
    const res = await axios.get(`${BASE}/export`, {
      params: { ...filtersToParams(filters), sortBy: sort.sortBy, sortOrder: sort.sortOrder, format, columns: columns.join(',') },
      responseType: 'blob',
      // файл формируется минутами: общий таймаут axios здесь не нужен, обрыв - через signal
      timeout: 0,
      signal
    });
    const blob: Blob = res.data;
    return { blob, filename: fileNameFrom(res.headers['content-disposition'], blob.type || String(res.headers['content-type'] || ''), format) };
  }
};

export const isCancelled = (err: unknown): boolean => axios.isCancel(err);

/** 403 - нет права (раздел, аналитика или выгрузка) */
export const isForbidden = (err: unknown): boolean => (err as { response?: { status?: number } })?.response?.status === 403;
